// frontend/src/components/shop/CreateDeliveryModal.jsx
import { useState, useEffect, useRef } from 'react';
import { createDelivery, getProducts, getDistricts } from '../../services/shopService';
import { districtAPI } from '../../services/api';

const CreateDeliveryModal = ({ isOpen, onClose, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [khoroos, setKhoroos] = useState([]);
  const [khoroosLoading, setKhoroosLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [productSearch, setProductSearch] = useState('');
  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v1';
  
  // Google Maps Autocomplete ref
  const addressInputRef = useRef(null);

  // Form data
  const [formData, setFormData] = useState({
    // Step 1: Customer Info
    customer_name: '',  // Заавал биш
    customer_phone: '',  // Заавал
    customer_phone_2: '',  // Нэмэлт утас (Заавал биш)
    delivery_address: '',
    district_id: '',
    khoroo_id: '',

    // Step 2: Product Info
    items: [],
    
    // Step 3: Payment
    payment_method: 'cod',  // 'prepaid', 'cod', 'custom'
    cod_amount: 0,
    notes: ''
  });

  // ============================================
  // GOOGLE MAPS AUTOCOMPLETE
  // ============================================
  useEffect(() => {
  if (!isOpen || !addressInputRef.current) return;
  if (!districts || districts.length === 0) return;
  
  if (typeof window.google === 'undefined' || !window.google.maps) {
    console.warn('⚠️ Google Maps API not loaded yet');
    return;
  }
  
  // ============================================
  // CHECK IF GOOGLE MAPS ENABLED & AVAILABLE
  // ============================================
  const checkAndInitialize = async () => {
    try {
      // Check status
      const statusResponse = await fetch(`${API_BASE}/shop/google-maps-status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const statusResult = await statusResponse.json();
      
      if (!statusResult.success || !statusResult.data.isAvailable) {
        console.warn('⚠️ Google Maps unavailable:', {
          enabled: statusResult.data?.enabled,
          remaining: statusResult.data?.remaining
        });
        
        // Show warning to user
        if (!statusResult.data?.enabled) {
          console.log('🚫 Google Maps API идэвхгүй болсон');
        } else if (statusResult.data?.remaining <= 0) {
          console.log('🚫 Google Maps өдрийн хязгаарт хүрсэн');
        }
        
        return; // Don't initialize autocomplete
      }
      
      console.log('✅ Google Maps available, initializing autocomplete...');
      
      // ============================================
      // INITIALIZE AUTOCOMPLETE
      // ============================================
      const autocomplete = new window.google.maps.places.Autocomplete(
        addressInputRef.current,
        {
          componentRestrictions: { country: 'mn' },
          fields: ['formatted_address', 'geometry', 'name', 'address_components']
        }
      );
      
      autocomplete.addListener('place_changed', async () => {
        const place = autocomplete.getPlace();
        
        if (!place.formatted_address) return;
        
        // ============================================
        // TRACK USAGE VIA BACKEND
        // ============================================
        try {
          const response = await fetch('http://localhost:5000/api/v1/shop/geocode', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ 
              address: place.formatted_address 
            })
          });
          
          const result = await response.json();
          
          if (result.success) {
            console.log('✅ Google Maps usage tracked:', result.usage);
            
            if (result.usage.remaining < 100) {
              console.warn(`⚠️ Google Maps хязгаарт ойртож байна: ${result.usage.remaining} үлдсэн`);
            }
          } else if (response.status === 429) {
            alert('⚠️ Google Maps өдрийн хязгаарт хүрлээ. Хаягаа гараар оруулна уу.');
            return;
          } else if (response.status === 403) {
            alert('⚠️ Google Maps API идэвхгүй болсон. Хаягаа гараар оруулна уу.');
            return;
          }
        } catch (err) {
          console.warn('⚠️ Failed to track usage:', err);
        }
        
        // ============================================
        // EXTRACT DISTRICT FROM ADDRESS COMPONENTS
        // ============================================
        let districtName = '';
        let detectedDistrict = null;
        
        if (place.address_components) {
          for (const component of place.address_components) {
            if (component.types.includes('sublocality_level_1') || 
                component.types.includes('sublocality') ||
                component.types.includes('locality')) {
              districtName = component.long_name;
              console.log('📍 Detected district component:', component);
              break;
            }
          }
          
          console.log('📍 Final detected district name:', districtName);
          
          if (districtName) {
            detectedDistrict = districts.find(d => {
              const dbName = d.name.toLowerCase().trim();
              const googleName = districtName.toLowerCase().trim();
              
              return dbName === googleName || 
                     dbName.includes(googleName) || 
                     googleName.includes(dbName);
            });
            
            if (detectedDistrict) {
              console.log('✅ District auto-matched:', detectedDistrict.name, '(ID:', detectedDistrict.id, ')');
            } else {
              console.warn('⚠️ District not matched in database:', districtName);
              console.warn('Available districts:', districts.map(d => d.name));
            }
          }
        }
        
        // ============================================
        // FORM UPDATE
        // ============================================
        // Plus Code (WWC7+Q99 гэх мэт) хасах
          let cleanAddress = place.formatted_address;
          
          // Plus Code pattern: XXX+XXX эсвэл XXXX+XXX
          const plusCodeRegex = /^[A-Z0-9]{4,8}\+[A-Z0-9]{2,4}\s*/i;
          cleanAddress = cleanAddress.replace(plusCodeRegex, '').trim();
          
          // Хэрэв place.name байвал тэрийг эхэнд нэмэх (газрын нэр)
          if (place.name && !cleanAddress.toLowerCase().includes(place.name.toLowerCase())) {
            cleanAddress = `${place.name}, ${cleanAddress}`;
          }
          
          setFormData(prev => ({
            ...prev,
            delivery_address: cleanAddress,
            district_id: detectedDistrict ? detectedDistrict.id : prev.district_id
          }));
        
        // Clear errors
        if (errors.delivery_address) {
          setErrors(prev => ({ ...prev, delivery_address: '' }));
        }
        if (detectedDistrict && errors.district_id) {
          setErrors(prev => ({ ...prev, district_id: '' }));
        }
      });
      
      console.log('✅ Google Maps Autocomplete initialized with enabled check');
      
    } catch (error) {
      console.error('❌ Google Maps check/init error:', error);
    }
  };
  
  checkAndInitialize();
  
}, [isOpen, districts, errors.delivery_address, errors.district_id]);

  // Fetch products and districts
  useEffect(() => {
    if (isOpen) {
      fetchProducts();
      fetchDistricts();
    }
  }, [isOpen]);

  const fetchProducts = async () => {
    try {
      const response = await getProducts({ limit: 100 });
      if (response.success) {
        // shopProductController.getShopProducts нь response.data дотор array буцаадаг
        setProducts(response.data || []);
      }
    } catch (error) {
      console.error('Fetch products error:', error);
    }
  };

  const fetchDistricts = async () => {
    try {
      console.log('📍 Fetching districts...');
      const response = await getDistricts();
      console.log('📍 Districts response:', response);
      if (response.success) {
        console.log('✅ Districts loaded:', response.data);
        setDistricts(response.data || []);
      }
    } catch (error) {
      console.error('❌ Fetch districts error:', error);
    }
  };

   // Handle input change
  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Дүүрэг сонгоход хороод татах
    if (name === 'district_id' && value) {
      try {
        setKhoroosLoading(true);
        setKhoroos([]);
        setFormData(prev => ({ ...prev, khoroo_id: '' }));
        const response = await districtAPI.getKhoroosByDistrict(value);
        if (response.data?.success) {
          setKhoroos(response.data.data || []);
        }
      } catch (error) {
        console.error('Хороод татахад алдаа:', error);
      } finally {
        setKhoroosLoading(false);
      }
    }
  };

  // Add item
  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { product_id: '', quantity: 1, unit_price: 0 }]
    }));
  };

  // Update item
  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    // Auto-fill price when product is selected
    if (field === 'product_id' && value) {
      const product = products.find(p => p.id === parseInt(value));
      if (product) {
        newItems[index].unit_price = product.base_price || 0;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      items: newItems
    }));
  };

  // Remove item
  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Calculate total items amount (NO delivery fee)
  const calculateItemsTotal = () => {
    return formData.items.reduce((sum, item) => 
      sum + (item.quantity * item.unit_price), 0
    );
  };

  // Calculate COD amount based on payment method
  const calculateCODAmount = () => {
    const itemsTotal = calculateItemsTotal();
    
    if (formData.payment_method === 'prepaid') {
      return 0;
    } else if (formData.payment_method === 'cod') {
      return itemsTotal;
    } else {
      return parseInt(formData.cod_amount) || 0;
    }
  };

  // Filter products by search
  const filteredProducts = products.filter(p => 
    p.product_name?.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.sku?.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.barcode?.toLowerCase().includes(productSearch.toLowerCase())
  );

  // Validate step
  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      // Нэр заавал биш болгосон
      // if (!formData.customer_name.trim()) newErrors.customer_name = 'Нэр оруулна уу';
      
      if (!formData.customer_phone.trim()) newErrors.customer_phone = 'Утас оруулна уу';
      if (!/^[0-9]{8}$/.test(formData.customer_phone)) {
        newErrors.customer_phone = 'Зөв утасны дугаар оруулна уу (8 орон)';
      }
      
      // Нэмэлт утас (optional) - Validation зөвхөн оруулсан үед
      if (formData.customer_phone_2 && !/^[0-9]{8}$/.test(formData.customer_phone_2)) {
        newErrors.customer_phone_2 = 'Зөв утасны дугаар оруулна уу (8 орон)';
      }
      
       if (!formData.delivery_address.trim()) newErrors.delivery_address = 'Хаяг оруулна уу';
        if (!formData.district_id) newErrors.district_id = 'Дүүрэг сонгоно уу';
        if (formData.district_id && !formData.khoroo_id) newErrors.khoroo_id = 'Хороо сонгоно уу';
      }
    
    if (step === 2) {
      if (formData.items.length === 0) {
        newErrors.items = 'Хамгийн багадаа 1 бараа нэмнэ үү';
      } else {
        formData.items.forEach((item, index) => {
          if (!item.product_id) newErrors[`item_${index}_product`] = 'Бараа сонгоно уу';
          if (item.quantity <= 0) newErrors[`item_${index}_quantity`] = 'Тоо ширхэг 0-ээс их байх ёстой';
        });
      }
    }

    if (step === 3) {
      if (formData.payment_method === 'custom') {
        const codAmount = parseInt(formData.cod_amount) || 0;
        const itemsTotal = calculateItemsTotal();
        if (codAmount < 0) newErrors.cod_amount = 'Дүн 0-ээс их байх ёстой';
        if (codAmount > itemsTotal) newErrors.cod_amount = 'Дүн барааны үнээс их байж болохгүй';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle next
  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  // Handle back
  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    try {
      setLoading(true);
      
      const payload = {
        customer_name: formData.customer_name || 'Хэрэглэгч',  // Default value
        customer_phone: formData.customer_phone,
        customer_phone_2: formData.customer_phone_2 || null,  // Optional
        address: formData.delivery_address,
        district_id: parseInt(formData.district_id),
        khoroo_id: formData.khoroo_id ? parseInt(formData.khoroo_id) : null,
        items: formData.items.map(item => ({
          product_id: parseInt(item.product_id),
          quantity: parseInt(item.quantity),
          unit_price: parseFloat(item.unit_price)
        })),
        // Борлуулалтын дүн (prepaid үед ч барааны дүнг хадгална)
        cod_amount: calculateCODAmount(),
        payment_status: formData.payment_method === 'prepaid' ? 'prepaid' : 'unpaid',
        notes: formData.notes
      };
      
      console.log('📦 Submitting delivery:', payload);
      const response = await createDelivery(payload);
      
      if (response.success) {
        alert('✅ Хүргэлт амжилттай шивэгдлээ!');
        onSuccess();
      } else {
        alert('❌ ' + (response.message || 'Хүргэлт шивэхэд алдаа гарлаа'));
      }
    } catch (error) {
      console.error('Submit error:', error);
      console.error('Error response:', error.response?.data);
      alert('❌ ' + (error.response?.data?.message || 'Хүргэлт шивэхэд алдаа гарлаа'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
              <h3 className="text-lg font-bold text-blue-900 mb-1 flex items-center gap-2">
                <span className="text-2xl">👤</span>
                <span>Хэрэглэгчийн мэдээлэл</span>
              </h3>
              <p className="text-sm text-blue-700">Хүргэлт хүлээн авах хүний мэдээлэл</p>
            </div>

            {/* Customer Name - NOT REQUIRED */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Нэр
              </label>
              <input
                type="text"
                name="customer_name"
                value={formData.customer_name}
                onChange={handleChange}
                placeholder="Овог нэр (заавал биш)"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 transition"
              />
            </div>

            {/* Customer Phone - REQUIRED */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Утасны дугаар <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="customer_phone"
                value={formData.customer_phone}
                onChange={handleChange}
                placeholder="99119911"
                maxLength={8}
                className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-primary-500 transition ${
                  errors.customer_phone ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
              />
              {errors.customer_phone && (
                <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                  <span>⚠️</span>
                  <span>{errors.customer_phone}</span>
                </p>
              )}
            </div>

            {/* Customer Phone 2 - OPTIONAL */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Нэмэлт утас
              </label>
              <input
                type="text"
                name="customer_phone_2"
                value={formData.customer_phone_2}
                onChange={handleChange}
                placeholder="88998899 (заавал биш)"
                maxLength={8}
                className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-primary-500 transition ${
                  errors.customer_phone_2 ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
              />
              {errors.customer_phone_2 && (
                <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                  <span>⚠️</span>
                  <span>{errors.customer_phone_2}</span>
                </p>
              )}
            </div>

            {/* District */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Дүүрэг <span className="text-red-500">*</span>
              </label>
              <select
                name="district_id"
                value={formData.district_id}
                onChange={handleChange}
                className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-primary-500 transition ${
                  errors.district_id ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
              >
                <option value="">Дүүрэг сонгох</option>
                {districts.map(district => (
                  <option key={district.id} value={district.id}>
                    {district.name}
                  </option>
                ))}
              </select>
               {errors.district_id && (
                <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                  <span>⚠️</span>
                  <span>{errors.district_id}</span>
                </p>
              )}
            </div>

            {/* Khoroo */}
            {formData.district_id && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Хороо <span className="text-red-500">*</span>
                  </label>
                 <select
                    name="khoroo_id"
                    value={formData.khoroo_id}
                    onChange={handleChange}
                    disabled={khoroosLoading}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-primary-500 transition ${
                      errors.khoroo_id ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  >
                  <option value="">
                      {khoroosLoading ? 'Уншиж байна...' : 'Хороо сонгох'}
                    </option>
                  {khoroos.map(khoroo => (
                    <option key={khoroo.id} value={khoroo.id}>
                      {khoroo.khoroo_number}-р хороо
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Delivery Address - WITH AUTOCOMPLETE */}
            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                Хаяг <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                <input
                    ref={addressInputRef}
                    type="text"
                    name="delivery_address"
                    value={formData.delivery_address}
                    onChange={handleChange}
                    placeholder="Хаяг бичиж эхлэх (автоматаар санал болгоно)"
                    autoComplete="off"
                    className={`w-full px-4 py-3 pr-12 border-2 rounded-xl focus:ring-2 focus:ring-primary-500 transition ${
                    errors.delivery_address ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                />
                <div className="absolute top-1/2 -translate-y-1/2 right-3 text-gray-400 pointer-events-none">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                </div>
                </div>
                {errors.delivery_address && (
                <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                    <span>⚠️</span>
                    <span>{errors.delivery_address}</span>
                </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                💡 Хаяг бичиж эхлэхэд Google Maps-аас автоматаар санал болгоно
                </p>
            </div>
            </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
              <h3 className="text-lg font-bold text-green-900 mb-1 flex items-center gap-2">
                <span className="text-2xl">📦</span>
                <span>Барааны мэдээлэл</span>
              </h3>
              <p className="text-sm text-green-700">Хүргэх бараануудыг нэмэх</p>
            </div>

            {/* Product Search */}
            <div>
              <input
                type="text"
                placeholder="🔍 Бараа хайх (нэр, код, barcode)"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 transition"
              />
            </div>

            {errors.items && (
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-center gap-3">
                <span className="text-2xl">⚠️</span>
                <p className="text-sm text-red-700 font-medium">{errors.items}</p>
              </div>
            )}

            {/* Items */}
            <div className="space-y-4">
              {formData.items.map((item, index) => {
                const selectedProduct = products.find(p => p.id === parseInt(item.product_id));
                
                return (
                  <div key={index} className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-bold text-gray-900">Бараа #{index + 1}</h4>
                      <button
                        onClick={() => removeItem(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                        type="button"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Product */}
                      <div className="md:col-span-2">
                        <label className="block text-xs font-semibold text-gray-700 mb-2">Бараа</label>
                        <select
                          value={item.product_id}
                          onChange={(e) => updateItem(index, 'product_id', e.target.value)}
                          className={`w-full px-3 py-2 border-2 rounded-lg focus:ring-2 focus:ring-primary-500 transition ${
                            errors[`item_${index}_product`] ? 'border-red-300 bg-red-50' : 'border-gray-300'
                          }`}
                        >
                          <option value="">Бараа сонгох</option>
                          {filteredProducts.map(product => (
                            <option key={product.id} value={product.id}>
                              {product.product_name} - {product.base_price?.toLocaleString()}₮ (Үлдэгдэл: {product.stock_quantity || 0})
                            </option>
                          ))}
                        </select>
                        {errors[`item_${index}_product`] && (
                          <p className="text-xs text-red-600 mt-1">{errors[`item_${index}_product`]}</p>
                        )}
                      </div>

                      {/* Quantity */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-2">Тоо ширхэг</label>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                          className={`w-full px-3 py-2 border-2 rounded-lg focus:ring-2 focus:ring-primary-500 transition ${
                            errors[`item_${index}_quantity`] ? 'border-red-300 bg-red-50' : 'border-gray-300'
                          }`}
                        />
                        {errors[`item_${index}_quantity`] && (
                          <p className="text-xs text-red-600 mt-1">{errors[`item_${index}_quantity`]}</p>
                        )}
                      </div>
                    </div>

                    {/* Price Info */}
                    {selectedProduct && (
                      <div className="mt-3 pt-3 border-t border-gray-300 space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Нэгжийн үнэ:</span>
                          <span className="font-bold text-gray-900">{item.unit_price.toLocaleString()}₮</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Үлдэгдэл:</span>
                          <span className={`font-semibold ${selectedProduct.stock_quantity > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {selectedProduct.stock_quantity || 0} ш
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Нийт үнэ:</span>
                          <span className="text-base font-bold text-primary-600">
                            {(item.quantity * item.unit_price).toLocaleString()}₮
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Add Item Button */}
            <button
              onClick={addItem}
              type="button"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:shadow-lg transition font-semibold"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Бараа нэмэх</span>
            </button>

            {/* Summary */}
            {formData.items.length > 0 && (
              <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-200">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-gray-900">Нийт дүн:</span>
                  <span className="font-bold text-2xl text-primary-600">
                    {calculateItemsTotal().toLocaleString()}₮
                  </span>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  * Хүргэлтийн төлбөр хүргэлтийн компанид тусад нь төлөгдөнө
                </p>
              </div>
            )}
          </div>
        );

      case 3:
        const itemsTotal = calculateItemsTotal();
        const codAmount = calculateCODAmount();

        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
              <h3 className="text-lg font-bold text-purple-900 mb-1 flex items-center gap-2">
                <span className="text-2xl">✅</span>
                <span>Баталгаажуулалт</span>
              </h3>
              <p className="text-sm text-purple-700">Мэдээллээ дахин шалгана уу</p>
            </div>

            {/* Customer Info Summary */}
            <div className="bg-white rounded-xl p-5 border-2 border-gray-200">
              <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span>👤</span>
                <span>Хэрэглэгчийн мэдээлэл</span>
              </h4>
              <div className="space-y-3 text-sm">
                {formData.customer_name && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Нэр:</span>
                    <span className="font-semibold text-gray-900">{formData.customer_name}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Утас:</span>
                  <span className="font-semibold text-gray-900">{formData.customer_phone}</span>
                </div>
                {formData.customer_phone_2 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Нэмэлт утас:</span>
                    <span className="font-semibold text-gray-900">{formData.customer_phone_2}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Дүүрэг:</span>
                  <span className="font-semibold text-gray-900">
                    {districts.find(d => d.id === parseInt(formData.district_id))?.name || 'Тодорхойгүй'}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-gray-600 mb-1">Хаяг:</span>
                  <span className="font-semibold text-gray-900 bg-gray-50 p-2 rounded">{formData.delivery_address}</span>
                </div>
              </div>
            </div>

            {/* Items Summary */}
            <div className="bg-white rounded-xl p-5 border-2 border-gray-200">
              <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span>📦</span>
                <span>Барааны мэдээлэл</span>
              </h4>
              <div className="space-y-3">
                {formData.items.map((item, index) => {
                  const product = products.find(p => p.id === parseInt(item.product_id));
                  return (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900">{product?.product_name}</p>
                        <p className="text-xs text-gray-600">{item.quantity} ш × {item.unit_price.toLocaleString()}₮</p>
                      </div>
                      <span className="text-sm font-bold text-primary-600">
                        {(item.quantity * item.unit_price).toLocaleString()}₮
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Payment Method */}
            <div className="bg-white rounded-xl p-5 border-2 border-gray-200">
              <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span>💰</span>
                <span>Төлбөрийн хэлбэр</span>
              </h4>
              
              <div className="space-y-3">
                {/* Prepaid */}
                <label className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition ${
                  formData.payment_method === 'prepaid' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="payment_method"
                    value="prepaid"
                    checked={formData.payment_method === 'prepaid'}
                    onChange={handleChange}
                    className="w-5 h-5 text-green-600"
                  />
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">Урьдчилсан төлбөр</p>
                    <p className="text-xs text-gray-600">Захиалагч аль хэдийн төлсөн</p>
                  </div>
                  {formData.payment_method === 'prepaid' && (
                    <span className="text-green-600 font-bold">Авах: 0₮</span>
                  )}
                </label>

                {/* COD */}
                <label className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition ${
                  formData.payment_method === 'cod' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="payment_method"
                    value="cod"
                    checked={formData.payment_method === 'cod'}
                    onChange={handleChange}
                    className="w-5 h-5 text-blue-600"
                  />
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">Хүргэлтээр төлөх</p>
                    <p className="text-xs text-gray-600">Захиалагч бүрэн дүнг хүргэлтээр төлнө</p>
                  </div>
                  {formData.payment_method === 'cod' && (
                    <span className="text-blue-600 font-bold">Авах: {itemsTotal.toLocaleString()}₮</span>
                  )}
                </label>

                {/* Custom */}
                <label className={`flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition ${
                  formData.payment_method === 'custom' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="payment_method"
                    value="custom"
                    checked={formData.payment_method === 'custom'}
                    onChange={handleChange}
                    className="w-5 h-5 text-purple-600 mt-1"
                  />
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 mb-2">Гараас үнэ оруулах</p>
                    <p className="text-xs text-gray-600 mb-3">Захиалагчаас авах дүнг гараар оруулах</p>
                    
                    {formData.payment_method === 'custom' && (
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                          Захиалагчаас авах дүн (₮)
                        </label>
                        <input
                          type="number"
                          name="cod_amount"
                          value={formData.cod_amount}
                          onChange={handleChange}
                          min="0"
                          max={itemsTotal}
                          placeholder="0"
                          className={`w-full px-3 py-2 border-2 rounded-lg focus:ring-2 focus:ring-purple-500 transition ${
                            errors.cod_amount ? 'border-red-300 bg-red-50' : 'border-gray-300'
                          }`}
                        />
                        {errors.cod_amount && (
                          <p className="text-xs text-red-600 mt-1">{errors.cod_amount}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          Хамгийн ихдээ: {itemsTotal.toLocaleString()}₮
                        </p>
                      </div>
                    )}
                  </div>
                </label>
              </div>
            </div>

            {/* Payment Summary */}
            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-5 border-2 border-green-200">
              <h4 className="text-sm font-bold text-green-900 mb-4 flex items-center gap-2">
                <span>💵</span>
                <span>Төлбөрийн дүн</span>
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-green-700">Барааны нийт дүн:</span>
                  <span className="font-semibold text-green-900">{itemsTotal.toLocaleString()}₮</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-700">Хүргэлтийн төлбөр:</span>
                  <span className="font-semibold text-green-900">Компанид тусад</span>
                </div>
                <div className="pt-3 border-t-2 border-green-300 flex justify-between items-center">
                  <span className="font-bold text-green-900 text-base">Захиалагчаас авах:</span>
                  <span className="font-bold text-2xl text-green-900">{codAmount.toLocaleString()}₮</span>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Нэмэлт тэмдэглэл</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Тэмдэглэл (заавал биш)"
                rows={3}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 transition resize-none"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-4xl w-full my-8 shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-primary-600 to-primary-500 text-white px-6 py-5 rounded-t-2xl z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">📦 Хүргэлт шивэх</h2>
              <p className="text-sm text-primary-100 mt-1">Алхам {currentStep}/3</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white hover:bg-white/20 rounded-lg transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mt-4 flex items-center gap-2">
            {[1, 2, 3].map(step => (
              <div
                key={step}
                className={`flex-1 h-2 rounded-full transition-all duration-300 ${
                  step <= currentStep ? 'bg-white' : 'bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-2xl flex justify-between gap-3">
          <button
            onClick={currentStep === 1 ? onClose : handleBack}
            className="px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition font-semibold"
            disabled={loading}
          >
            {currentStep === 1 ? 'Болих' : 'Буцах'}
          </button>
          
          {currentStep < 3 ? (
            <button
              onClick={handleNext}
              className="px-8 py-3 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-xl hover:shadow-lg transition font-semibold flex items-center gap-2"
            >
              <span>Үргэлжлүүлэх</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-8 py-3 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-xl hover:shadow-lg transition font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Шивэж байна...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Баталгаажуулах</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateDeliveryModal;